import React, { useState } from 'react';
import {
  ShoppingBag,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { getCategoryInfo } from '../../utils/formatters';

export default function ListView({ products = [] }) {
  const [sortField, setSortField] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'title') {
      cmp = a.title.localeCompare(b.title, 'vi');
    } else if (sortField === 'platform') {
      cmp = (a.platform || '').localeCompare(b.platform || '');
    } else if (sortField === 'category') {
      cmp = (a.category_name || '').localeCompare(b.category_name || '');
    }
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown size={11} className="opacity-0 group-hover:opacity-40 transition" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp size={11} className="text-[#F5A623]" />
    ) : (
      <ArrowDown size={11} className="text-[#F5A623]" />
    );
  };

  if (!products || products.length === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center p-8 text-center text-amber-200/60">
        <p className="text-sm font-medium">Không tìm thấy bao lì xì nào phù hợp.</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto select-none pb-12">
      <table className="w-full text-left text-xs border-collapse">
        {/* Table Header */}
        <thead>
          <tr className="h-10 border-b border-[#6E141E] bg-[#2E060A] text-[11px] text-amber-200/90 font-semibold sticky top-0 z-20">
            <th className="w-14 px-3 py-1.5 text-center">Ảnh</th>
            <th
              onClick={() => handleSort('title')}
              className="px-3 py-1.5 cursor-pointer group hover:text-[#FFF5E6] transition"
            >
              <div className="flex items-center gap-1.5">
                <span>Tên sản phẩm</span>
                {renderSortIndicator('title')}
              </div>
            </th>
            <th
              onClick={() => handleSort('category')}
              className="hidden sm:table-cell w-52 px-3 py-1.5 cursor-pointer group hover:text-[#FFF5E6] transition"
            >
              <div className="flex items-center gap-1.5">
                <span>Danh mục</span>
                {renderSortIndicator('category')}
              </div>
            </th>
            <th
              onClick={() => handleSort('platform')}
              className="w-28 px-3 py-1.5 cursor-pointer group hover:text-[#FFF5E6] transition text-center"
            >
              <div className="flex items-center justify-center gap-1.5">
                <span>Nền tảng</span>
                {renderSortIndicator('platform')}
              </div>
            </th>
            <th className="w-36 px-3 py-1.5 text-center">Ưu đãi</th>
          </tr>
        </thead>

        {/* Table Rows */}
        <tbody className="divide-y divide-[#4D0A11]">
          {sortedProducts.map((p) => {
            const targetUrl = p.affiliate_url || p.target_url || '#';
            const catInfo = getCategoryInfo(p.category_slug || p.category_name);

            return (
              <tr
                key={p.id}
                onClick={() => {
                  if (targetUrl && targetUrl !== '#') {
                    window.open(targetUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
                className="h-14 hover:bg-[#3D080E] cursor-pointer transition group"
              >
                {/* Thumbnail */}
                <td className="px-3 py-1.5 text-center">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#2D060A] mx-auto border border-[#7A1018]">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                      loading="lazy"
                    />
                  </div>
                </td>

                {/* Title */}
                <td className="px-3 py-1.5">
                  <div className="font-medium text-[#FFF5EB] line-clamp-2 group-hover:text-amber-200 transition-colors">
                    {p.title}
                  </div>
                </td>

                {/* Category with Icon */}
                <td className="hidden sm:table-cell px-3 py-1.5 text-amber-200/80">
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <span>{catInfo.icon}</span>
                    <span>{catInfo.name}</span>
                  </span>
                </td>

                {/* Platform */}
                <td className="px-3 py-1.5 text-center">
                  {p.platform === 'shopee' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#EE4D2D] text-white">
                      <ShoppingBag size={10} /> Shopee
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-black/90 text-white border border-white/20">
                      <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.29 0 .58.04.85.12V9.35a6.34 6.34 0 0 0-.85-.06A6.33 6.33 0 0 0 3 15.62a6.34 6.34 0 0 0 9.17 5.63 6.34 6.34 0 0 0 3.6-5.63V8.82a8.28 8.28 0 0 0 3.82.93V6.69z"/>
                      </svg>
                      <span>TikTok</span>
                    </span>
                  )}
                </td>

                {/* Voucher Button */}
                <td className="px-3 py-1.5 text-center">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] group-hover:brightness-110 text-[11px] font-extrabold transition shadow-xs">
                    <span>Nhận Voucher</span>
                    <ExternalLink size={11} />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
